
import { Component, Ref } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import EventHeader from '@/components/event/EventHeader.vue';
import EventFlow from '@/active-components/event-flow/EventFlow.vue';
import { EventResponse } from '@/model/Event';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import CustomModal from '@/components/modal/CustomModal.vue';
import CreateEventModal from '@/active-components/dashboard/CreateEventModal.vue';
import EventLoaderMixin from '@/mixins/EventLoaderMixin';
import EventFlowImportModal from '@/components/event-flow/EventFlowImportModal.vue';
import ToastMixin from '@/mixins/ToastMixin';
import ToastVariant from '@/model/enums/toast/toast-variant';
import {
    ExcelImportRequest,
    ExcelImportResult,
} from '@/model/event-flow/EventFlow';

@Component({
    components: {
        EventHeader,
        EventFlow,
        CustomModal,
        CreateEventModal,
        EventFlowImportModal,
    },
    computed: {
        ...mapGetters({
            getEvent: 'eventStorage/getEvent',
        }),
    },
    methods: {
        ...mapActions({
            fetchEventById: 'eventStorage/fetchEventById',
            downloadExcelTemplate: 'eventFlowStorage/downloadExcelTemplate',
            importExcel: 'eventFlowStorage/importExcel',
        }),
        ...mapMutations({
            setEvent: 'eventStorage/setEvent',
        }),
    },
})
export default class Events extends mixins(EventLoaderMixin, ToastMixin) {
    @Ref() readonly eventFlowRef!: EventFlow;

    downloadExcelTemplate!: () => Promise<void>;

    importExcel!: (request: ExcelImportRequest) => Promise<ExcelImportResult>;

    importResult: ExcelImportResult | null = null;

    importingExcel = false;

    downloadingExcelTemplate = false;

    onShowNewItemModal(): void {
        this.eventFlowRef.onCreate();
    }

    onShowDuplicateModal(): void {
        this.$bvModal.show('duplicate-event-modal');
    }

    onShowImportModal(): void {
        this.importResult = null;
        this.$bvModal.show('event-flow-import-modal');
    }

    onImport(file: File | null): void {
        if (!file) {
            return;
        }

        this.importingExcel = true;
        this.importExcel({ eventId: this.eventId, file })
            .then((result: ExcelImportResult) => {
                this.importResult = result;
                return this.eventFlowRef.reloadAfterImport().catch(() => {
                    this.showToast(
                        this.$t('event-flow.import.refresh-error').toString(),
                        this.$t(
                            'event-flow.import.refresh-error-title',
                        ).toString(),
                        ToastVariant.DANGER,
                    );
                });
            })
            .catch(() => {
                this.showToast(
                    this.$t('event-flow.import.upload-error').toString(),
                    this.$t('event-flow.import.upload-error-title').toString(),
                    ToastVariant.DANGER,
                );
            })
            .finally(() => {
                this.importingExcel = false;
            });
    }

    onResetImport(): void {
        this.importResult = null;
    }

    onInvalidFile(): void {
        this.showToast(
            this.$t('event-flow.import.invalid-file').toString(),
            this.$t('event-flow.import.invalid-file-title').toString(),
            ToastVariant.DANGER,
        );
    }

    onDownloadTemplate(): void {
        this.downloadingExcelTemplate = true;
        this.downloadExcelTemplate()
            .catch(() => {
                this.showToast(
                    this.$t('event-flow.import.download-error').toString(),
                    this.$t(
                        'event-flow.import.download-error-title',
                    ).toString(),
                    ToastVariant.DANGER,
                );
            })
            .finally(() => {
                this.downloadingExcelTemplate = false;
            });
    }

    onCloseDuplicate(): void {
        this.$bvModal.hide('duplicate-event-modal');
    }

    onDuplicateSuccess(createdEvent?: EventResponse): void {
        this.$bvModal.hide('duplicate-event-modal');
        if (createdEvent?.id) {
            this.$router.push({
                name: 'Event',
                params: { id: createdEvent.id.toString() },
            });
        }
    }
}
